import { useRef, useState } from "react";
import { Card } from "@/components/ui-helpers";
import { Code2, GripVertical, Plus, Trash2 } from "lucide-react";

// Admin-only "Developer mode": edit the option lists behind the app's dropdowns.
// Removing an option only stops it being picked from now on — records already holding
// the old value keep showing it everywhere.
export const DEVELOPER_OPTION_DOMAINS = [
  { key: "client_statuses", label: "Client statuses", hint: "Pipeline order on the Clients page and a client's profile." },
  { key: "stages", label: "Case stages", hint: "Ordered stages a case moves through; drives Active / Submitted / Closed folders." },
  { key: "services", label: "Immigration services", hint: "Service choices when creating a case." },
  { key: "interaction_channels", label: "Interaction channels", hint: "How a client was helped in the “Worked on by” log." },
  { key: "decision_outcomes", label: "Decision outcomes", hint: "Officer decision choices. “Other” is the only one that does not complete a case." },
];

function OptionListEditor({ domain, value, onChange }) {
  const list = value || [];
  const [draft, setDraft] = useState("");
  const dragFrom = useRef(null);
  const id = domain.key.replace(/_/g, "-");

  const rename = (i, v) => onChange(list.map((x, j) => (j === i ? v : x)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const add = () => {
    const v = draft.trim();
    if (!v || list.includes(v)) { setDraft(""); return; }
    onChange([...list, v]);
    setDraft("");
  };
  const drop = (to) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from === null || from === to) return;
    const next = [...list];
    next.splice(to, 0, next.splice(from, 1)[0]);
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`dev-options-${id}`}>
      <div className="font-semibold text-sm text-slate-900">{domain.label}</div>
      <p className="text-[11px] text-slate-500 mt-0.5 mb-3">{domain.hint}</p>
      <div className="space-y-1.5">
        {list.map((opt, i) => (
          <div
            key={`${i}-${opt}`}
            draggable
            onDragStart={() => { dragFrom.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(i)}
            data-testid={`dev-option-row-${id}-${i}`}
            className="flex items-center gap-1.5 group"
          >
            <GripVertical className="h-4 w-4 text-slate-300 cursor-grab shrink-0 group-hover:text-slate-400 transition-colors" />
            <input
              data-testid={`dev-option-input-${id}-${i}`}
              value={opt}
              onChange={(e) => rename(i, e.target.value)}
              className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-primary outline-none transition-colors"
            />
            <button
              data-testid={`dev-option-remove-${id}-${i}`}
              onClick={() => remove(i)}
              title={`Remove ${opt}`}
              className="text-slate-300 hover:text-rose-600 px-1 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-400">No options — add one below.</div>}
      </div>
      <div className="flex gap-2 mt-3">
        <input
          data-testid={`dev-option-new-${id}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="New option…"
          className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm"
        />
        <button
          data-testid={`dev-option-add-${id}`}
          onClick={add}
          className="inline-flex items-center gap-1 bg-slate-900 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

export default function DeveloperModeCard({ settings, set }) {
  const on = !!settings.developer_mode;
  return (
    <Card className="p-5 space-y-4 mt-5" data-testid="developer-mode-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-primary" />
            <h3 className="font-head font-bold text-slate-900">Developer mode</h3>
          </div>
          <p className="text-xs text-slate-500 max-w-2xl mt-0.5">
            Turn this on to add, rename, reorder or remove the options behind the app's dropdowns.
            Removing an option only stops it being picked from now on — clients and cases already using it keep showing the old value.
          </p>
        </div>
        <button
          role="switch"
          aria-checked={on}
          data-testid="developer-mode-toggle"
          onClick={() => set({ developer_mode: !on })}
          className={`relative h-7 w-12 rounded-full shrink-0 transition-colors ${on ? "bg-primary" : "bg-slate-200"}`}
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] duration-200 ${on ? "left-6" : "left-1"}`} />
        </button>
      </div>
      {on ? (
        <>
          <div className="grid lg:grid-cols-2 gap-4" data-testid="developer-option-editors">
            {DEVELOPER_OPTION_DOMAINS.map((dom) => (
              <OptionListEditor key={dom.key} domain={dom} value={settings[dom.key]} onChange={(v) => set({ [dom.key]: v })} />
            ))}
          </div>
          <p className="text-[11px] text-slate-400" data-testid="developer-mode-hint">
            Click “Save changes” at the top to apply. Duplicates and blanks are dropped automatically; a list can never be left empty.
          </p>
        </>
      ) : (
        <p className="text-xs text-slate-400" data-testid="developer-mode-off-note">
          Developer mode is off — dropdowns work normally and the option editors stay hidden.
        </p>
      )}
    </Card>
  );
}
